import { Box, Container, Flex, Grid } from 'components/box';
import { CategoryPage } from 'components/_pages/category/types';
import ProductListing from 'components/_shared/widgets/product-listing';
import SectionTitle from 'components/_shared/elements/section-title';
import { css } from '@emotion/react';
import asTitleCase from '../../../../util/as-title-case';
import { Link } from 'components/configurable-routing';
import { Text } from 'components/typography';
import MoreProductsFooter from 'components/_shared/widgets/more-products-footer';
import { File } from 'types/types';
import resourceUrl from '../../../../util/make-url';
import RichText from 'components/rich-text';
import Empty from './empty';

/**
 * similar to the asTitleCase function, only it handles hyphens and
 * replaces "And" with the ampersand symbol in category pages.
 */
const idAsTitleCase = (id: string) =>
  id
    .toLowerCase()
    .replaceAll('-', ' ')
    .replaceAll('and', '&')
    .split(' ')
    .map(word => `${word.charAt(0).toUpperCase()}${word.substring(1)}`)
    .join(' ');

const Header = ({ title }: { title: string }) => (
  <Box px={3}>
    <Container
      py={3}
      px={0}
      css={theme => css`
        max-width: ${theme.extendedMaxWidth}px;
      `}
    >
      <Flex
        flexDirection="column"
        css={theme => css`
          gap: ${theme.space[3]}px;
        `}
      >
        <Flex
          alignItems="center"
          css={theme => css`
            gap: ${theme.space[1]}px;
          `}
        >
          <Link href="/" fontWeight="normal">
            Today&apos;s Deals
          </Link>
          <Text>/</Text>
          <Text>{title}</Text>
        </Flex>

        <SectionTitle title={title} />
      </Flex>
    </Container>
  </Box>
);

const CategoryBanner = ({
  categoryName,
  banner,
}: {
  categoryName: string;
  banner: File;
}) => {
  const image = resourceUrl(banner.url, {
    isAssetUrl: false,
    quality: 75,
    width: 1200,
  });

  return (
    <Box
      tabIndex={0}
      m={0}
      p={0}
      css={theme => css`
        border-radius: ${theme.radii[3]}px;
        overflow: hidden;
      `}
    >
      <img
        aria-label={`${categoryName} Banner`}
        alt={`${categoryName} Banner`}
        src={image}
        css={css`
          width: 100%;
          margin: 0;
          padding: 0;
        `}
      />
    </Box>
  );
};

const CategoryDescription = ({ description }: { description: string }) => (
  <Box px={3} mt={[3, null, 4]} mb={4}>
    <Container py={3}>
      <Flex
        flexDirection="column"
        css={theme => css`
          gap: ${theme.space[3]}px;

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            font-weight: 700 !important;
            font-family: ${theme.fonts.header} !important;
            line-height: 1.2 !important;
            color: ${theme.colors.black} !important;

            width: 100% !important;
            line-height: 1.1;
            padding-bottom: 0.8em;
          }

          h1 {
            font-size: ${theme.fontSizes[4]};
          }

          h2 {
            font-size: ${theme.fontSizes[3]};
          }

          h3,
          h4,
          h5 {
            font-size: ${theme.fontSizes[2]};
          }

          p,
          li {
            line-height: 1.6em !important;
            width: 100% !important;
          }

          p {
            margin-bottom: 0.8em !important;
          }

          p:last-child {
            margin-bottom: 0 !important;
          }

          a {
            color: ${theme.colors.primary};
            text-decoration: none;
          }

          ul,
          ol {
            margin: 0 0 1em;
            padding-left: 2em;
            width: 100% !important;
          }

          ul {
            list-style-type: disc;
          }

          ol {
            list-style-type: decimal;
          }

          img {
            max-width: 100%;
          }

          @media ${theme.mediaQueries.mobileDown} {
            p {
              line-height: 1.4rem !important;
              margin-bottom: 0.5em !important;
              font-size: 0.9rem !important;
            }
          }
        `}
      >
        <RichText content={description} />
      </Flex>
    </Container>
  </Box>
);

const Body = ({ banner, category, categoryId, ...props }: CategoryPage) => (
  <>
    <Header
      title={category ? asTitleCase(category.name) : idAsTitleCase(categoryId)}
    />

    <Container py={3}>
      {props.items.length === 0 ? (
        <Empty />
      ) : (
        <Grid>
          {!!banner && (
            <CategoryBanner
              categoryName={
                category
                  ? asTitleCase(category.name)
                  : idAsTitleCase(categoryId)
              }
              banner={banner}
            />
          )}
          <ProductListing {...props} />
        </Grid>
      )}
    </Container>

    {/* NOTE: duplicating this here and disabling for this page layout so that we can put the copy underneath it */}
    <MoreProductsFooter message="Looking for more deals?" />

    {!!category && category.description && (
      <CategoryDescription description={category.description} />
    )}
  </>
);

export default Body;
